import type React from "react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { BaseComposeInput } from "@/components/common/BaseComposeInput";
import type { EditableFile } from "@/components/common/ExistingFileAttachment";
import { ExistingFileAttachment } from "@/components/common/ExistingFileAttachment";
import { FileAttachment } from "@/components/common/FileAttachment";
import {
  type EditableFileOps,
  useEditableAttachments,
} from "@/components/common/hooks/useEditableAttachments";
import {
  COMMENT_MAX_FILE_COUNT,
  COMMENT_MAX_FILE_SIZE,
  COMMENT_MAX_LENGTH,
} from "@/constants";

interface CommentEditFormProps {
  value: string;
  isSubmitting: boolean;
  onChange: (value: string) => void;
  onSave: (value: string, fileOps: EditableFileOps) => void | Promise<void>;
  onCancel: () => void;
  showMentionWarning: boolean;
  existingFiles?: EditableFile[];
}

export const CommentEditForm: React.FC<CommentEditFormProps> = ({
  value,
  isSubmitting,
  onChange,
  onSave,
  onCancel,
  showMentionWarning,
  existingFiles = [],
}) => {
  const { t } = useTranslation();
  const initialValue = useRef(value).current;
  const attachments = useEditableAttachments();

  const handleCancel = () => {
    attachments.reset();
    onCancel();
  };

  return (
    <div className="space-y-2 text-sm font-normal text-variable-collection-black-gray">
      {existingFiles.length > 0 && (
        <ExistingFileAttachment
          files={existingFiles}
          filesToRemove={attachments.filesToRemove}
          filesToReplace={attachments.filesToReplace}
          onRemove={attachments.removeExistingFile}
          onReplace={attachments.replaceExistingFile}
        />
      )}
      <BaseComposeInput
        initialContent={value}
        onContentChange={onChange}
        onSubmit={async (content) => {
          onChange(content);
          await onSave(content, attachments.fileOps);
        }}
        onCancel={handleCancel}
        submitButtonLabel={t("common.save")}
        cancelButtonLabel={t("common.cancel")}
        showFormatting
        showAttachButton
        externalOnAttachClick={() =>
          attachments.addFilesInputRef.current?.click()
        }
        externalFileInputRef={attachments.addFilesInputRef}
        externalOnFileChange={attachments.addFilesInputChange}
        hasDiscardableChanges={
          attachments.hasFileChanges || value.trim() !== initialValue.trim()
        }
        alwaysVisible
        autoFocus
        compact
        context="post"
        disabled={isSubmitting}
        maxFileSize={COMMENT_MAX_FILE_SIZE}
        maxFileCount={COMMENT_MAX_FILE_COUNT}
        maxLength={COMMENT_MAX_LENGTH}
        containerClassNameOverride="rounded-lg border-slate-200 bg-white shadow-none"
      />
      {attachments.filesToAdd.length > 0 && (
        <FileAttachment
          files={attachments.filesToAdd}
          fileError={null}
          onRemoveFile={attachments.removeNewFile}
        />
      )}
      {showMentionWarning && (
        <div className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 text-sm text-amber-700">
          {t("common.editMentionNoNotificationWarning")}
        </div>
      )}
    </div>
  );
};
